import { z } from 'zod';

import { type DynamicWidgetBuildContext } from '@apps/web/components/phoebe_chat_view/dynamic_widgets/dynamic_widget_registry';
import {
    buildBindingPresentation,
    dynamicWidgetEventSourceSchema,
    DynamicWidgetModelBuilder,
    dynamicWidgetResourceSourceSchema,
    getDynamicWidgetSourceId,
} from '@apps/web/components/phoebe_chat_view/dynamic_widgets/dynamic_widget_schema';
import { type ToolExecutionEntry } from '@apps/web/components/phoebe_chat_view/transcript_types';

const WORKSPACE_FILE_API_PATH =
    '/organizations/{organization_id}/phoebe_agent_runs/{phoebe_agent_run_id}/workspace-files/{file_path}';

const RETRIEVE_DOMAINS = [
    'shifts',
    'outreach_history',
    'caregivers',
    'clients',
] as const;

const RETRIEVE_TOOL_NAME_BY_DOMAIN: Readonly<Record<string, string>> = {
    shifts: 'retrieve_shifts',
    outreach_history: 'retrieve_outreach_history',
    caregivers: 'retrieve_caregivers',
    clients: 'retrieve_clients',
};

const retrieveEnvelopeFieldsSchema = z.object({
    // These fields were added with the domain-typed retrieval tools. Keep
    // them optional so saved conversations from the generic `retrieve` era
    // continue to render.
    domain: z.enum(RETRIEVE_DOMAINS).optional(),
    request_hash: z
        .string()
        .regex(/^[a-f0-9]{64}$/)
        .optional(),
    presentation_intent: z
        .enum(['auto', 'overview', 'scan', 'inspect', 'compare', 'monitor'])
        .optional()
        .default('auto'),
    artifact_path: z.string().min(1),
    format: z.literal('jsonl'),
    row_count: z.number().int().nonnegative(),
    schema_hint: z.record(z.string(), z.string()),
    // New envelopes call this field `preview`. Keep `head_preview` for saved
    // transcripts and normalize both names to the legacy consumer shape.
    preview: z.array(z.unknown()).optional(),
    head_preview: z.array(z.unknown()).optional(),
    // The Anthropic extractor strips None values from stored tool outputs
    // (llm_framework/anthropic/extractor.py), so a last-page envelope
    // arrives with the key absent rather than null.
    next_cursor: z
        .string()
        .nullish()
        .transform((value) => value ?? null),
});

export const retrieveEnvelopeSchema = retrieveEnvelopeFieldsSchema
    .superRefine((envelope, context) => {
        if (
            envelope.preview === undefined &&
            envelope.head_preview === undefined
        ) {
            context.addIssue({
                code: z.ZodIssueCode.custom,
                message: 'retrieve envelope requires preview or head_preview',
                path: ['preview'],
            });
        }
    })
    .transform((envelope) => {
        const preview = envelope.preview ?? envelope.head_preview ?? [];
        return {
            ...envelope,
            preview,
            head_preview: preview,
        };
    });

export type RetrieveEnvelope = z.infer<typeof retrieveEnvelopeSchema>;

export type RetrieveArtifactBuild = {
    readonly envelope: RetrieveEnvelope;
    readonly workspacePath: string;
    readonly eventSourceId: string;
    readonly resourceSourceId: string;
    readonly builder: DynamicWidgetModelBuilder;
};

function isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/**
 * Registry predicates use only the committed arguments. They deliberately do
 * no envelope validation or artifact reads, keeping eligibility cheap enough
 * to run for every completed transcript entry.
 */
export function matchesRetrieveDomain(
    entry: ToolExecutionEntry,
    domain: string,
): boolean {
    if (entry.name === RETRIEVE_TOOL_NAME_BY_DOMAIN[domain]) {
        return true;
    }
    return (
        entry.name === 'retrieve' &&
        isRecord(entry.arguments) &&
        entry.arguments['domain'] === domain
    );
}

function normalizeRetrieveArtifactPath(
    artifactPath: string,
    domain: string,
): string | null {
    const trimmed = artifactPath.trim();
    const prefix = `/workspace/retrieval/${domain}/`;
    if (!trimmed.startsWith(prefix)) {
        return null;
    }
    const suffix = trimmed.slice('/workspace/'.length);
    if (
        suffix.length === 0 ||
        suffix
            .split('/')
            .some(
                (part) =>
                    part.length === 0 ||
                    part === '.' ||
                    part === '..' ||
                    !/^[A-Za-z0-9._-]+$/.test(part),
            )
    ) {
        return null;
    }
    return suffix;
}

export function buildRetrieveArtifact(
    entry: ToolExecutionEntry,
    context: DynamicWidgetBuildContext,
    domain: string,
): RetrieveArtifactBuild | null {
    if (
        !matchesRetrieveDomain(entry, domain) ||
        entry.callId === null ||
        !entry.toolCallEventId ||
        !entry.runId
    ) {
        return null;
    }
    const envelope = retrieveEnvelopeSchema.parse(entry.output);
    if (envelope.domain !== undefined && envelope.domain !== domain) {
        return null;
    }
    const workspacePath = normalizeRetrieveArtifactPath(
        envelope.artifact_path,
        domain,
    );
    if (workspacePath === null) {
        return null;
    }
    const eventSource = dynamicWidgetEventSourceSchema.parse({
        kind: 'event_source',
        toolName: entry.name,
        callId: entry.callId,
        toolCallEventId: entry.toolCallEventId,
        toolOutputEventId: entry.id,
        phase: 'output',
        capturedAt: entry.createdAt,
        organizationId: context.organizationId,
        mode: context.mode,
    });
    const resourceSource = dynamicWidgetResourceSourceSchema.parse({
        kind: 'resource_source',
        resourceType: 'agent_run_workspace_file',
        resourceId: `${entry.runId}:${workspacePath}`,
        apiPath: WORKSPACE_FILE_API_PATH,
        fetchedAt: null,
        organizationId: context.organizationId,
        mode: context.mode,
    });
    const sources = [eventSource, resourceSource] as const;
    return {
        envelope,
        workspacePath,
        eventSourceId: getDynamicWidgetSourceId(eventSource),
        resourceSourceId: getDynamicWidgetSourceId(resourceSource),
        builder: new DynamicWidgetModelBuilder(sources),
    };
}

export function addRetrieveEnvelopeBindings({
    artifact,
}: {
    readonly artifact: RetrieveArtifactBuild;
}): void {
    artifact.builder.addSourceField({
        id: 'rowCount',
        sourceId: artifact.eventSourceId,
        path: '/row_count',
        rawValue: artifact.envelope.row_count,
        value: artifact.envelope.row_count,
        renderedValue: String(artifact.envelope.row_count),
        presentation: buildBindingPresentation({
            section: 'card',
            label: 'rowCount',
        }),
    });
    artifact.builder.addSourceField({
        id: 'workspaceFile.path',
        sourceId: artifact.eventSourceId,
        path: '/artifact_path',
        rawValue: artifact.envelope.artifact_path,
        value: artifact.workspacePath,
        renderedValue: artifact.workspacePath,
        presentation: buildBindingPresentation({
            section: 'workspaceFile',
            label: 'path',
        }),
    });
    artifact.builder.addSourceField({
        id: 'previewRows',
        sourceId: artifact.eventSourceId,
        path: '/head_preview',
        rawValue: artifact.envelope.head_preview,
        value: artifact.envelope.head_preview,
        renderedValue: `${artifact.envelope.head_preview.length} preview rows`,
        presentation: buildBindingPresentation({
            section: 'rows',
            label: 'previewRows',
        }),
    });
    artifact.builder.addLiveField({
        id: 'resourceRows',
        sourceIds: [artifact.resourceSourceId],
        paths: ['/rows'],
        presentation: buildBindingPresentation({
            section: 'rows',
            label: 'resourceRows',
        }),
    });
}
